import { App } from '@capacitor/app'
import { PushNotifications } from '@capacitor/push-notifications'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { SimCall } from '@/native/simCall'
import { registerDevice } from '@/services/api/device.service'
import { useAuthStore } from '@/stores/auth.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { logger } from '@/utils/logger'
import { isNativeApp } from '@/utils/platform'

import { installationId } from './deviceIdentity'
import { dispatchPushAction, onPushAction, setPushToken } from './pushState'
import { registerWebPush } from './webPush'

/** Android channel for general notifications (the calling feature creates its own `calls` channel). */
const NOTIFICATIONS_CHANNEL = 'notifications'

async function registerNativeDevice(token: string): Promise<void> {
  const info = await App.getInfo()
  await registerDevice({
    installationId: await installationId(),
    platform: 'ANDROID',
    deviceLabel: info.name,
    appVersion: info.version,
    fcmToken: token,
  })
}

/** Android app: asks for permission, makes the notifications channel, saves the push token. */
function setUpNativePush(onTap: (link: string) => void): () => void {
  let cancelled = false
  const handles: { remove: () => Promise<void> }[] = []
  // A listener that resolves after cleanup ran is removed at once instead of leaking.
  const keep = (handle: { remove: () => Promise<void> }) => {
    if (cancelled) void handle.remove()
    else handles.push(handle)
  }
  const stopTapHandler = onPushAction('notification', (data) => {
    if (data.link) onTap(data.link)
  })

  void (async () => {
    try {
      if (!(await SimCall.pushConfigured()).configured) {
        logger.warn('Push notifications are not configured in this build (no google-services.json)')
        return
      }
      let permission = await PushNotifications.checkPermissions()
      if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') permission = await PushNotifications.requestPermissions()
      if (permission.receive !== 'granted' || cancelled) return
      await PushNotifications.createChannel({
        id: NOTIFICATIONS_CHANNEL,
        name: 'Notifications',
        description: 'Messages from automations and the workspace',
        importance: 4,
        visibility: 1,
        vibration: true,
      })
      keep(await PushNotifications.addListener('registration', (token) => {
        setPushToken(token.value)
        void registerNativeDevice(token.value).catch((error) => logger.warn('Could not save the push token', error))
      }))
      keep(await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        dispatchPushAction(action.notification.data as Record<string, string> | undefined)
      }))
      if (cancelled) return
      await PushNotifications.register()
    } catch (error) {
      logger.warn('Push notifications unavailable', error)
    }
  })()

  return () => {
    cancelled = true
    stopTapHandler()
    handles.forEach((handle) => void handle.remove())
  }
}

/**
 * Registers this device for push for everyone who is signed in, in the Android app (always) and in a
 * browser (only after the person has turned browser notifications on). Mounted once in the app shell;
 * independent of calling, which adds its own `calls` channel and tap handler on top.
 */
export function usePushRegistration() {
  const accessToken = useAuthStore((s) => s.accessToken)
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)
  const navigate = useNavigate()

  useEffect(() => {
    if (!accessToken || !workspaceId) return
    if (isNativeApp()) {
      return setUpNativePush((link) => navigate(link))
    }
    // Browser: a no-op unless notifications are configured and switched on; never prompts.
    void registerWebPush().catch((error) => logger.warn('Browser push registration failed', error))
  }, [accessToken, workspaceId, navigate])
}
